import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import CabPage from "../CabPage";
import { useCabUser } from "../CabShell";
import { usePlaces, geocodeCity, detectMyCity } from "../places";
import "../css/addvehicle.css";
import useFleet, {
  BODY_TYPE_OPTIONS,
  CURRENCIES,
  RADIUS_OPTIONS,
  STATUS_META,
  currencySign,
  deriveState,
  plural,
} from "../fleet/useFleet";
import { Ic, VehiclePhoto, MobileNav, Dialog, useToast, NoFleetAccess } from "../fleet/ui";
import {
  HM_RE,
  VIN_MSG,
  VIN_RE,
  apiError,
  checkDocFile,
  createTransport,
  dmyToIso,
  fmtDmy,
  fmtHm,
  parseDmy,
  patchTransport,
  readFile,
  shortName,
} from "../fleet/api";
import { refreshUnread } from "../useUnread";
import { setTruckAvailable, fetchMyRequests, bulkStatus } from "../../utils/matchingApi";

const BRANDS = ["MAN", "Volvo", "Scania", "DAF", "Mercedes-Benz", "Iveco", "Renault", "КамАЗ", "МАЗ", "Howo", "Shacman", "Foton"];

// wireframe `state.set('draft')` (localStorage key itruck-add-vehicle:draft)
const DRAFT_KEY = "itruck-add-vehicle:draft";
const readDraft = () => {
  try {
    return JSON.parse(localStorage.getItem(DRAFT_KEY));
  } catch {
    return null;
  }
};
const writeDraft = (d) => {
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(d));
    return true;
  } catch {
    return false;
  }
};
const clearDraft = () => {
  try {
    localStorage.removeItem(DRAFT_KEY);
  } catch {
    /* ignore */
  }
};

// the three document rows of section 3 (wireframe: Техпаспорт, Страховка, Лицензия)
const DOCS = [
  { key: "passport", title: "Техпаспорт", field: "tech_passport_of_tractor", have: "passport_doc" },
  { key: "insurance", title: "Страховка", field: "insurance_photo", have: "insurance_doc" },
  { key: "licence", title: "Лицензия", field: "licence_photo", have: "licence_doc" },
];

const blankForm = () => {
  const now = new Date();
  const until = new Date(now.getTime() + 7 * 86_400_000);
  return {
    brand: "",
    model: "",
    plate: "",
    body: "",
    capacity: "",
    volume: "",
    driver: "",
    phone: "",
    vin: "",
    city: "",
    radius: 200,
    available: fmtDmy(now),
    time: fmtHm(now),
    destination: "",
    rate: "",
    currency: 4,
    until: fmtDmy(until),
    until_time: "23:00",
    visible: true,
    comment: "",
    verified: false,
    transfer: false,
    urgent: false,
  };
};

/** section 1 values of an existing fleet vehicle */
const vehiclePatch = (v) => ({
  brand: v.mark === "—" ? "" : v.mark,
  model: v.model && v.model !== v.mark ? v.model : "",
  plate: v.plate === "—" ? "" : v.plate,
  body: v.body_type_id ? String(v.body_type_id) : "",
  capacity: v.max_load_weight ?? "",
  volume: v.max_load_volume ?? "",
  driver: v.driver_name || "",
  phone: v.driver_phone || "",
  vin: v.vin || "",
  transfer: !!v.can_transfer,
  urgent: !!v.ready_urgent,
});

/** section 2/3 values from the vehicle's last published availability */
const availPatch = (v) => {
  if (!v.live) return {};
  const from = v.available_from ? new Date(v.available_from) : null;
  const until = v.available_until ? new Date(v.available_until) : null;
  return {
    city: v.city || "",
    radius: v.radius_km ?? 200,
    destination: (v.destinations || []).map((d) => d.city).join(", "),
    rate: v.min_price != null ? String(v.min_price) : "",
    currency: v.currency ?? 4,
    comment: v.comment || "",
    visible: v.status !== "INACTIVE",
    ...(from && from.getTime() > Date.now() ? { available: fmtDmy(from), time: fmtHm(from) } : {}),
    ...(until && until.getTime() > Date.now() ? { until: fmtDmy(until), until_time: fmtHm(until) } : {}),
  };
};

const titleOf = (v) => [v.mark, v.model && v.model !== v.mark ? v.model : ""].filter(Boolean).join(" ");

export default function AddvehiclePage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { name: ownerName } = useCabUser();
  const { canFleet, loading, vehicles, load } = useFleet();
  const [toast, toastNode] = useToast();
  const formRef = useRef(null);
  const cityRef = useRef(null);
  const destRef = useRef(null);
  // coordinates from Google Places (spec §6.3: city + coordinates when available); lets the truck show up on the maps
  const [geo, setGeo] = useState({ city: null, dest: {} });

  const idsParam = useMemo(() => {
    const raw = params.get("vehicles") || params.get("vehicle") || "";
    return raw.split(",").map((x) => parseInt(x, 10)).filter(Boolean);
  }, [params]);
  const bulk = idsParam.length > 1;

  const [sel, setSel] = useState(params.get("new") ? "new" : ""); // "new" | vehicle_id (string)
  const [m, setM] = useState(blankForm);
  const [docs, setDocs] = useState({}); // chosen files {passport: File, ...}
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [dlg, setDlg] = useState(null); // {type: cancel|request|details|document|saved}
  const createdRef = useRef(null); // id of the transport created by this form (never POST it twice)

  const set = (patch) => setM((p) => ({ ...p, ...patch }));

  // initial mode: NEW by default (always when there are no trucks); ?vehicle=ID -> that truck
  const initDone = useRef(false);
  useEffect(() => {
    if (loading || initDone.current) return;
    initDone.current = true;
    if (bulk) {
      const first = vehicles.find((v) => v.vehicle_id === idsParam[0]);
      if (first) setM((p) => ({ ...p, ...availPatch(first) }));
      return;
    }
    const one = idsParam.length === 1 ? vehicles.find((v) => v.vehicle_id === idsParam[0]) : null;
    if (one) {
      setSel(String(one.vehicle_id));
      setM((p) => ({ ...p, ...vehiclePatch(one), ...availPatch(one) }));
      return;
    }
    setSel("new");
    const d = readDraft();
    if (d) setM((p) => ({ ...p, ...d }));
  }, [loading, vehicles, idsParam, bulk]);

  const vehicle = sel && sel !== "new" ? vehicles.find((v) => String(v.vehicle_id) === sel) : null;
  const isNew = !bulk && !vehicle;

  const choose = (value) => {
    setErr("");
    setDocs({});
    createdRef.current = null;
    setSel(value);
    if (value === "new") {
      const b = blankForm();
      setM((p) => ({
        ...p,
        ...Object.fromEntries(["brand", "model", "plate", "body", "capacity", "volume", "driver", "phone", "vin", "transfer", "urgent"].map((k) => [k, b[k]])),
      }));
    } else {
      const v = vehicles.find((x) => String(x.vehicle_id) === value);
      if (v) setM((p) => ({ ...p, ...vehiclePatch(v), ...availPatch(v) }));
    }
  };

  // ── wireframe update(): preview ────────────────────────────
  const bulkVehicles = bulk ? idsParam.map((id) => vehicles.find((v) => v.vehicle_id === id)).filter(Boolean) : [];
  const bodyName = BODY_TYPE_OPTIONS.find((o) => String(o.value) === String(m.body))?.label;
  const modelTitle = bulk ? `Выбрано машин: ${idsParam.length}` : [m.brand, m.model].filter(Boolean).join(" ") || "Новая машина";
  const spec = bulk
    ? bulkVehicles.map((v) => v.mark).join(", ") || "—"
    : [bodyName, m.capacity !== "" ? `${m.capacity} тонн` : null, m.volume !== "" ? `${m.volume} м³` : null].filter(Boolean).join(" • ") || "—";
  const plate = bulk ? bulkVehicles.map((v) => v.plate).join(", ") || "—" : m.plate || "—";
  const dmy = (s, t) => `${s || "—"}${t ? `, ${t}` : ""}`;
  const fromDate = parseDmy(m.available);
  const previewState = m.visible
    ? deriveState({ status: "ACTIVE", available_from: fromDate ? fromDate.toISOString() : null })
    : null;
  const radiusLabel = RADIUS_OPTIONS.find((r) => r.value === Number(m.radius))?.label || `${m.radius} км`;
  const rateLabel = `${Number(m.rate || 0).toLocaleString("ru-RU")} ${currencySign(Number(m.currency))}`;
  const destList = m.destination.split(",").map((s) => s.trim()).filter(Boolean);
  const destLabel = destList.length ? destList.join(", ") : "—";
  const driverShort = shortName(m.driver) || ownerName;
  const badge = <>{previewState ? `▣ ${STATUS_META[previewState].label}` : "Не в поиске"}</>;
  const previewVehicle = { mark: modelTitle, photo: vehicle?.photo };

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  // ── wireframe validateDates() ──────────────────────────────
  const validateDates = () => {
    const el = formRef.current.elements;
    const a = parseDmy(m.available);
    const b = parseDmy(m.until);
    el.available.setCustomValidity(a ? "" : "Введите дату в формате ДД.ММ.ГГГГ");
    el.until.setCustomValidity(b ? "" : "Введите дату в формате ДД.ММ.ГГГГ");
    if (a && b && b < a) el.until.setCustomValidity("Дата окончания должна быть не раньше даты начала");
    for (const k of ["time", "until_time"]) {
      const ok = k === "until_time" && !m[k] ? true : HM_RE.test(m[k]);
      el[k].setCustomValidity(ok ? "" : "Введите время в формате ЧЧ:ММ");
    }
    if (m.vin.trim() && !VIN_RE.test(m.vin.trim())) el.vin.setCustomValidity(VIN_MSG);
    else el.vin.setCustomValidity("");
    return formRef.current.reportValidity();
  };
  const clearValidity = (e) => e.target.setCustomValidity("");

  usePlaces(
    [
      {
        ref: cityRef,
        onPick: (p) => {
          set({ city: p.city || String(p.address || "").split(",")[0].trim() });
          setGeo((g) => ({ ...g, city: { lat: p.lat, lng: p.lng, country: p.country || null } }));
        },
      },
      {
        ref: destRef,
        onPick: (p) => {
          const city = p.city || String(p.address || "").split(",")[0].trim();
          setM((prev) => {
            const tokens = String(prev.destination || "").split(",").map((x) => x.trim());
            tokens[tokens.length - 1] = city; // the fragment being typed becomes the picked city
            return { ...prev, destination: tokens.filter(Boolean).join(", ") };
          });
          setGeo((g) => ({ ...g, dest: { ...g.dest, [city]: { lat: p.lat, lng: p.lng, country: p.country || null } } }));
        },
      },
    ],
    [loading, canFleet],
  );

  const useGps = async () => {
    try {
      const r = await detectMyCity();
      if (r.city) set({ city: r.city });
      setGeo((g) => ({ ...g, city: { lat: r.lat, lng: r.lng, country: r.country } }));
      toast(r.city ? `Определено: ${r.city}` : "Координаты определены, укажите город вручную");
    } catch (e) {
      toast(e.message);
    }
  };

  const bind = (name) => ({
    name,
    value: m[name],
    onChange: (e) => {
      clearValidity(e);
      set({ [name]: e.target.value });
    },
  });

  // ── actions ────────────────────────────────────────────────
  const saveDraft = () => {
    const ok = writeDraft(m);
    toast(ok ? "Черновик сохранён. Файлы документов нужно будет выбрать повторно." : "Не удалось сохранить черновик в браузере");
  };

  const resetForm = () => {
    for (const k of ["available", "until", "time", "until_time", "vin"]) formRef.current?.elements[k]?.setCustomValidity("");
    const b = blankForm();
    setDocs({});
    setErr("");
    if (vehicle) setM({ ...b, ...vehiclePatch(vehicle), ...availPatch(vehicle) });
    else setM(b);
    setDlg(null);
    toast("Форма сброшена");
  };

  const pickDoc = async (key, file) => {
    if (!file) {
      setDocs((p) => ({ ...p, [key]: null }));
      return;
    }
    const bad = checkDocFile(file);
    if (bad) return toast(bad);
    setDocs((p) => ({ ...p, [key]: file }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!validateDates()) return;
    setBusy(true);
    try {
      // 1) vehicle data: create (NEW) or update the chosen vehicle when section 1 was edited
      let targets;
      if (bulk) targets = idsParam;
      else if (vehicle) {
        targets = [vehicle.vehicle_id];
        const p = vehiclePatch(vehicle);
        const changed = {};
        const cmp = (k, apiKey, conv) => {
          if (String(m[k] ?? "") !== String(p[k] ?? "")) changed[apiKey] = conv(m[k]);
        };
        cmp("brand", "mark", (x) => x.trim());
        cmp("model", "model", (x) => x.trim() || null);
        cmp("plate", "government_number", (x) => x.trim());
        cmp("vin", "vin", (x) => (x.trim() ? x.trim().toUpperCase() : null));
        cmp("body", "body_type", (x) => (x ? Number(x) : null));
        cmp("capacity", "max_load_weight", (x) => (x !== "" ? Number(x) : null));
        cmp("volume", "max_load_volume", (x) => (x !== "" ? Number(x) : null));
        cmp("driver", "driver_name", (x) => x.trim() || null);
        cmp("phone", "driver_phone", (x) => x.trim() || null);
        if (m.transfer !== p.transfer) changed.can_transfer = !!m.transfer;
        if (m.urgent !== p.urgent) changed.ready_urgent = !!m.urgent;
        for (const d of DOCS) if (docs[d.key]) changed[d.field] = await readFile(docs[d.key]);
        if (Object.keys(changed).length) await patchTransport(vehicle.vehicle_id, changed);
      } else if (createdRef.current) {
        targets = [createdRef.current]; // created by an earlier submit of this form (never POST twice)
      } else {
        const body = {
          mark: m.brand.trim(),
          model: m.model.trim() || null,
          government_number: m.plate.trim(),
          vin: m.vin.trim() ? m.vin.trim().toUpperCase() : null,
          body_type: m.body ? Number(m.body) : null,
          max_load_weight: m.capacity !== "" ? Number(m.capacity) : null,
          max_load_volume: m.volume !== "" ? Number(m.volume) : null,
          driver_name: m.driver.trim() || null,
          driver_phone: m.phone.trim() || null,
          can_transfer: !!m.transfer,
          ready_urgent: !!m.urgent,
        };
        for (const d of DOCS) if (docs[d.key]) body[d.field] = await readFile(docs[d.key]);
        const created = await createTransport(body);
        createdRef.current = created.id;
        targets = [created.id];
      }

      // 2) availability (matching service)
      // coordinates: the picked suggestion / GPS, otherwise geocode the typed city (works only when Maps is loaded)
      const here = geo.city || (await geocodeCity(m.city));
      const dests = await Promise.all(
        destList.map(async (c) => {
          const g = geo.dest[c] || (await geocodeCity(c));
          return { city: c, ...(g && g.country ? { country: g.country } : {}) };
        }),
      );
      const payload = {
        city: m.city.trim(),
        ...(here ? { lat: here.lat, lng: here.lng, ...(here.country ? { country: here.country } : {}) } : {}),
        available_from: dmyToIso(m.available, m.time),
        available_until: dmyToIso(m.until, m.until_time || "23:59"),
        destinations: dests,
        radius_km: Number(m.radius),
        comment: m.comment.trim() || undefined,
      };
      if (m.rate !== "" && Number(m.rate) > 0) {
        payload.min_price = Number(m.rate);
        payload.currency = Number(m.currency);
      }
      const results = await Promise.all(targets.map((vehicle_id) => setTruckAvailable({ ...payload, vehicle_id })));
      // «Показывать машину в поиске» off = availability INACTIVE
      if (!m.visible) await bulkStatus(targets, "INACTIVE");
      clearDraft();
      load();
      refreshUnread();

      if (targets.length === 1) {
        const vid = targets[0];
        let count = (results[0]?.matched_loads?.top || []).filter((x) => x.vehicle_id === vid).length;
        try {
          const feed = await fetchMyRequests();
          count = (feed?.matches || []).filter((x) => x.vehicle_id === vid).length;
        } catch {
          /* keep matched_loads.top */
        }
        setDlg({ type: "saved", count, title: modelTitle, plate, route: `${m.city} → ${destLabel}`, visible: m.visible });
        // the form now edits the saved vehicle: a second submit updates it, never creates a duplicate
        setSel(String(vid));
        setDocs({});
      } else {
        toast(`Опубликовано машин: ${targets.length}`);
        setTimeout(() => navigate("/my-fleet"), 900);
      }
    } catch (e2) {
      setErr(
        createdRef.current
          ? `Машина создана, но доступность не опубликована: ${apiError(e2)} Повторите отправку — машина не будет создана заново.`
          : apiError(e2, "Не удалось опубликовать"),
      );
    } finally {
      setBusy(false);
    }
  };

  // ── render ─────────────────────────────────────────────────
  const heading = (
    <header className="page-heading">
      <h1>{bulk ? "Обновить состояние машин" : params.get("vehicle") ? "Изменить машину" : "Добавить свободную машину"}</h1>
      <p>Добавьте машину в поиск, чтобы получать запросы от грузоотправителей и диспетчеров.</p>
    </header>
  );

  if (!canFleet) {
    return (
      <CabPage name="addvehicle">
        <NoFleetAccess />
        <MobileNav />
      </CabPage>
    );
  }

  const req = isNew; // wireframe `required` applies when creating a vehicle
  const docRow = (d) => {
    const file = docs[d.key];
    const server = vehicle?.[d.have];
    return (
      <div className="doc-row" key={d.key}>
        <span>{d.title}</span>
        <label className={`doc-file${file || server ? " attached" : ""}`}>
          <input accept=".jpg,.jpeg,.png,image/*" name={`doc${d.key}`} onChange={(e) => { pickDoc(d.key, e.target.files?.[0]); e.target.value = ""; }} type="file" />
          <span>{file ? `${file.name} ✓` : server ? "Загружено ✓" : "Загрузить файл"}</span>
        </label>
        <button aria-label={`Действия с документом ${d.title}`} className="doc-more" data-action="document" data-doc={d.key} onClick={() => setDlg({ type: "document", d })} type="button">···</button>
      </div>
    );
  };

  return (
    <CabPage name="addvehicle">
      <main id="main">
        <div className="add-layout">
          <div className="form-column">
            {heading}

            {!bulk && vehicles.length > 0 && (
              <label className="field" style={{ maxWidth: 460, margin: "14px 0 0" }}>
                <span>Для какой машины</span>
                <span className="select-wrap">
                  <select aria-label="Для какой машины" onChange={(e) => choose(e.target.value)} value={sel || "new"}>
                    <option value="new">+ Новая машина</option>
                    {vehicles.map((v) => (
                      <option key={v.vehicle_id} value={v.vehicle_id}>{titleOf(v)} · {v.plate}</option>
                    ))}
                  </select>
                  <Ic n="chevron" />
                </span>
              </label>
            )}

            <nav aria-label="Разделы формы" className="steps">
              <button data-step="vehicle" onClick={() => scrollTo("vehicle")} type="button"><b>1</b>Данные машины</button>
              <i className="blue-line"></i>
              <button data-step="availability" onClick={() => scrollTo("availability")} type="button"><b>2</b>Доступность</button>
              <i></i>
              <button data-step="additional" onClick={() => scrollTo("additional")} type="button"><b>3</b>Публикация</button>
            </nav>

            <form id="vehicle-form" onSubmit={submit} ref={formRef}>
              <section className="form-block" id="vehicle">
                <h2><Ic n="truck" />1. Данные машины</h2>
                <div className="block-body">
                  {bulk ? (
                    <p style={{ fontSize: 13, color: "#4b6798", margin: "6px 0 8px" }}>
                      Состояние будет обновлено для выбранных машин: {idsParam.length}. Постоянные данные машин не меняются.
                    </p>
                  ) : (
                    <>
                      <div className="vehicle-grid">
                        <label className="field">
                          <span>Марка <b>*</b></span>
                          <span className="input-wrap">
                            <input aria-label="Марка" list="brand-list" required={req} type="text" {...bind("brand")} />
                            <datalist id="brand-list">{BRANDS.map((b) => <option key={b} value={b} />)}</datalist>
                          </span>
                        </label>
                        <label className="field">
                          <span>Модель <b>*</b></span>
                          <span className="input-wrap"><input aria-label="Модель" required={req} type="text" {...bind("model")} /></span>
                        </label>
                        <label className="field">
                          <span>Госномер <b>*</b></span>
                          <span className="input-wrap"><input aria-label="Госномер" required type="text" {...bind("plate")} /></span>
                        </label>
                        <label className="field">
                          <span>Тип кузова <b>*</b></span>
                          <span className="select-wrap has-icon">
                            <Ic n="truck" />
                            <select aria-label="Тип кузова" required={req} {...bind("body")}>
                              <option value="">—</option>
                              {BODY_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                            <Ic n="chevron" />
                          </span>
                        </label>
                        <label className="field">
                          <span>Грузоподъёмность <b>*</b></span>
                          <span className="input-wrap unit">
                            <input aria-label="Грузоподъёмность" min="0.1" required={req} step="any" type="number" {...bind("capacity")} />
                            <span>тонн</span>
                          </span>
                        </label>
                        <label className="field">
                          <span>Объём</span>
                          <span className="input-wrap unit">
                            <input aria-label="Объём" min="0.1" required={req} step="any" type="number" {...bind("volume")} />
                            <span>м³</span>
                          </span>
                        </label>
                        <label className="field">
                          <span>Водитель <b>*</b></span>
                          <span className="input-wrap"><input aria-label="Водитель" required={req} type="text" {...bind("driver")} /></span>
                        </label>
                        <label className="field">
                          <span>Контактный номер <b>*</b></span>
                          <span className="input-wrap"><input aria-label="Контактный номер" required={req} type="tel" {...bind("phone")} /></span>
                        </label>
                      </div>
                      <div className="vin-row">
                        <label className="field">
                          <span>VIN (необязательно)</span>
                          <span className="input-wrap"><input aria-label="VIN (необязательно)" maxLength={17} type="text" {...bind("vin")} /></span>
                        </label>
                      </div>
                    </>
                  )}
                </div>
              </section>

              <section className="form-block" id="availability">
                <h2><Ic n="calendar" />2. Доступность</h2>
                <div className="block-body">
                  <div className="availability-first">
                    <label className="field">
                      <span>Где сейчас находится машина <b>*</b></span>
                      <span className="input-wrap has-icon">
                        <Ic n="pin" />
                        <input
                          aria-label="Где сейчас находится машина"
                          autoComplete="off"
                          placeholder="Например, Бишкек"
                          ref={cityRef}
                          required
                          type="text"
                          {...bind("city")}
                          onChange={(e) => {
                            e.target.setCustomValidity("");
                            set({ city: e.target.value });
                            setGeo((g) => ({ ...g, city: null })); // typed by hand → coordinates are re-resolved on submit
                          }}
                        />
                        <button
                          className="link"
                          onClick={useGps}
                          style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontSize: 12, background: "none", border: 0, color: "#0063ff", cursor: "pointer" }}
                          title="Определить город по GPS"
                          type="button"
                        >
                          Определить
                        </button>
                      </span>
                    </label>
                    <label className="field">
                      <span>Радиус поиска груза <b>*</b></span>
                      <span className="select-wrap">
                        <select aria-label="Радиус поиска груза" name="radius" onChange={(e) => set({ radius: Number(e.target.value) })} value={m.radius}>
                          {RADIUS_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                        </select>
                        <Ic n="chevron" />
                      </span>
                    </label>
                    <label className="field">
                      <span>Свободен с <b>*</b></span>
                      <span className="input-wrap has-icon">
                        <Ic n="calendar" />
                        <input aria-label="Свободен с" placeholder="ДД.ММ.ГГГГ" required type="text" {...bind("available")} />
                      </span>
                    </label>
                    <label className="field">
                      <span>Время <b>*</b></span>
                      <span className="input-wrap has-icon">
                        <Ic n="clock" />
                        <input aria-label="Время" placeholder="ЧЧ:ММ" required type="text" {...bind("time")} />
                      </span>
                    </label>
                  </div>
                  <div className="availability-second">
                    <label className="field">
                      <span>Куда ищет груз <b>*</b></span>
                      <span className="input-wrap has-icon">
                        <Ic n="pin" />
                        <input aria-label="Куда ищет груз" autoComplete="off" placeholder="Бишкек, Алматы" ref={destRef} required type="text" {...bind("destination")} />
                      </span>
                    </label>
                    <label className="field">
                      <span>Минимальная ставка от</span>
                      <span className="input-wrap unit">
                        {/* optional per ТЗ §4.2 / product note (the wireframe prototype marks it required) */}
                        <input aria-label="Минимальная ставка от" min="0.1" step="any" type="number" {...bind("rate")} />
                        <span>{currencySign(Number(m.currency))}</span>
                      </span>
                    </label>
                    <label className="field">
                      <span>Валюта <b>*</b></span>
                      <span className="select-wrap">
                        <select aria-label="Валюта" name="currency" onChange={(e) => set({ currency: Number(e.target.value) })} value={m.currency}>
                          {CURRENCIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                        </select>
                        <Ic n="chevron" />
                      </span>
                    </label>
                    <label className="field">
                      <span>Актуально до <b>*</b></span>
                      <span className="input-wrap has-icon">
                        <Ic n="calendar" />
                        <input aria-label="Актуально до" placeholder="ДД.ММ.ГГГГ" required type="text" {...bind("until")} />
                      </span>
                    </label>
                    <label className="field">
                      <span>Время окончания</span>
                      <span className="input-wrap has-icon">
                        <Ic n="clock" />
                        <input aria-label="Время окончания" placeholder="ЧЧ:ММ" type="text" {...bind("until_time")} />
                      </span>
                    </label>
                  </div>
                  <label className="visibility-toggle">
                    <input checked={m.visible} name="visible" onChange={(e) => set({ visible: e.target.checked })} role="switch" type="checkbox" />
                    <i></i>
                    <span>
                      <strong>Показывать машину в поиске</strong>
                      <small>Ваша машина будет видна грузоотправителям и диспетчерам в результатах поиска.</small>
                    </span>
                  </label>
                </div>
              </section>

              <section className="form-block" id="additional">
                <h2><Ic n="doc" />3. Дополнительно<span>Документы (необязательно)</span></h2>
                <div className="block-body additional-grid">
                  <label className="comment field">
                    <span>Комментарий для грузоотправителя</span>
                    <textarea maxLength={1000} name="comment" onChange={(e) => set({ comment: e.target.value })} value={m.comment} />
                    <small id="comment-count">{m.comment.length}/1000</small>
                  </label>
                  <div className="checks">
                    <label style={{ opacity: 0.6 }} title="Статус «Проверена iTruck» присваивается платформой после проверки компании">
                      <input checked={m.verified} disabled name="verified" readOnly type="checkbox" />Верифицированный перевозчик ⓘ
                    </label>
                    <label><input checked={m.transfer} name="transfer" onChange={(e) => set({ transfer: e.target.checked })} type="checkbox" />Можно организовать перегрузку ⓘ</label>
                    <label><input checked={m.urgent} name="urgent" onChange={(e) => set({ urgent: e.target.checked })} type="checkbox" />Готов к срочной погрузке ⓘ</label>
                  </div>
                  <div className="documents">{DOCS.map(docRow)}</div>
                </div>
              </section>

              {err && (
                <p role="alert" style={{ margin: "8px 3px", padding: "9px 12px", borderRadius: 8, background: "#fff1f3", color: "#d6203b", fontSize: 13 }}>{err}</p>
              )}
              <footer className="form-actions">
                <button className="button draft" data-action="draft" onClick={saveDraft} type="button">Сохранить в черновик</button>
                <button className="button cancel" data-action="cancel" onClick={() => setDlg({ type: "cancel" })} type="button">Отмена</button>
                <button className="button primary" disabled={busy} type="submit">
                  <Ic n="truck" />
                  {busy ? "Публикуем…" : bulk ? "Обновить состояние" : "Опубликовать свободную машину"}
                </button>
              </footer>
            </form>
          </div>

          <aside className={`preview-column${m.visible ? "" : " is-hidden"}`}>
            <section className="preview-card">
              <h2><Ic n="search" />Предпросмотр<span className="badge">{badge}</span></h2>
              <div className="preview-body">
                <div className="truck-hero">
                  <VehiclePhoto className="hero-truck" v={previewVehicle} />
                  <div>
                    <strong>{modelTitle}</strong>
                    <small>{spec}</small>
                    <span className="plate">{plate}</span>
                  </div>
                </div>
                <div className="summary-grid">
                  <div className="summary-item">
                    <Ic n="pin" />
                    <div><small>Сейчас</small><strong>{m.city || "—"}</strong><span>Радиус <span>{radiusLabel}</span></span></div>
                  </div>
                  <div className="summary-item">
                    <Ic n="pin" />
                    <div><small>Куда ищет груз</small><strong>{destLabel}</strong></div>
                  </div>
                  <div className="summary-item">
                    <Ic n="calendar" />
                    <div><small>Свободен с</small><strong>{dmy(m.available, m.time)}</strong></div>
                  </div>
                  <div className="summary-item">
                    <Ic n="money" />
                    <div><small>Ставка от</small><b>{rateLabel}</b></div>
                  </div>
                  <div className="summary-item end-date">
                    <Ic n="calendar" />
                    <div><small>Актуально до</small><strong>{dmy(m.until, m.until_time)}</strong></div>
                  </div>
                </div>
              </div>
            </section>

            <section className="preview-card public-preview">
              <h2><Ic n="user" />Что увидят грузоотправители</h2>
              <div className="public-body">
                <div className="public-top">
                  <span className="driver-photo" style={{ display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "50%", background: "linear-gradient(135deg,#4476b9,#19427b)", color: "#fff", fontSize: 14 }}>
                    <Ic n="user" />
                  </span>
                  <div><strong>{driverShort}</strong></div>
                  <div className="public-model"><strong>{modelTitle}</strong><small>{spec}</small></div>
                  <VehiclePhoto className="mini-truck" v={previewVehicle} />
                  <span className="badge">{badge}</span>
                </div>
                <p className="public-route">
                  <Ic n="pin" />
                  <span>{m.city || "—"}</span> → <span>{destLabel}</span>
                </p>
                <div className="public-meta">
                  <span><Ic n="calendar" />с <span>{dmy(m.available.replace(/\.\d{4}$/, ""), m.time)}</span></span>
                  <span>◉ <span>{radiusLabel}</span></span>
                  <b>от <span>{rateLabel}</span></b>
                </div>
                <div className="public-actions">
                  <button className="button primary" data-action="request" onClick={() => setDlg({ type: "request" })} type="button">Отправить заявку</button>
                  <button aria-label="Карточка машины" className="button" data-action="details" onClick={() => setDlg({ type: "details" })} type="button">···</button>
                </div>
              </div>
            </section>

            <section className="preview-card tips">
              <h2><Ic n="bell" />Советы</h2>
              <ol>
                <li><b>1</b><p>Укажите актуальное местоположение машины<small>Это поможет найти ближайшие грузы и сократит ваши порожние пробеги.</small></p></li>
                <li><b>2</b><p>Чем точнее направление и ставка, тем выше шанс получить запрос<small>Грузоотправители чаще выбирают машины с чёткими параметрами.</small></p></li>
                <li><b>3</b><p>Не забудьте обновлять статус, если машина уже загрузилась<small>Это поможет избежать лишних звонков и повысит вашу репутацию.</small></p></li>
              </ol>
            </section>
          </aside>
        </div>
      </main>

      <MobileNav />

      {dlg?.type === "cancel" && (
        <Dialog onClose={() => setDlg(null)} title="Отменить изменения">
          <p>Сбросить форму к исходным значениям?</p>
          <div className="actions">
            <button className="button" onClick={() => setDlg(null)} type="button">Продолжить заполнение</button>
            <button className="button primary" data-action="reset" onClick={resetForm} type="button">Сбросить</button>
          </div>
        </Dialog>
      )}
      {dlg?.type === "request" && (
        <Dialog onClose={() => setDlg(null)} title="Предпросмотр заявки">
          <p>Такую кнопку увидит грузоотправитель в результатах поиска.</p>
          <p className="demo-note">Вы просматриваете свою машину — отправить заявку самому себе нельзя.</p>
        </Dialog>
      )}
      {dlg?.type === "details" && (
        <Dialog onClose={() => setDlg(null)} title="Карточка машины">
          <p><strong>{[m.brand, m.model].filter(Boolean).join(" ") || modelTitle}</strong></p>
          <p>{m.city} → {m.destination}</p>
          <p>{m.comment}</p>
        </Dialog>
      )}
      {dlg?.type === "document" && (
        <Dialog onClose={() => setDlg(null)} title="Документ">
          {docs[dlg.d.key] ? (
            <>
              <p>Выбран файл: {docs[dlg.d.key].name}</p>
              <p>Размер: {Math.round(docs[dlg.d.key].size / 1024)} КБ</p>
              <p className="demo-note">Файл будет загружен на сервер при публикации машины.</p>
            </>
          ) : vehicle?.[dlg.d.have] ? (
            <p>Документ «{dlg.d.title}» уже загружен.{" "}
              <a href={vehicle[dlg.d.have]} rel="noreferrer" style={{ color: "#0063ff" }} target="_blank">Открыть</a>
            </p>
          ) : (
            <p>Документ не загружен. Для загрузки нажмите на поле документа и выберите файл (JPG / PNG).</p>
          )}
        </Dialog>
      )}
      {dlg?.type === "saved" && (
        <Dialog onClose={() => setDlg(null)} title="Машина сохранена">
          <p><strong>{dlg.title} · {dlg.plate}</strong></p>
          <p>{dlg.route}</p>
          <p>{dlg.visible ? "Статус: ищет груз." : "Машина скрыта из поиска."}</p>
          {dlg.visible && (
            <p>
              {dlg.count > 0
                ? `Найдено ${dlg.count} ${plural(dlg.count, "подходящий груз", "подходящих груза", "подходящих грузов")}.`
                : "Пока нет подходящих грузов — пришлём уведомление, когда появится."}
            </p>
          )}
          <div className="actions">
            <button className="button primary" onClick={() => setDlg(null)} type="button">Готово</button>
            <button className="button" onClick={() => navigate("/my-fleet")} type="button">Перейти в «Мой транспорт»</button>
          </div>
        </Dialog>
      )}
      {toastNode}
    </CabPage>
  );
}
